/**
 * WizardNavigation
 *
 * Shared navigation component for the Generate Week wizard.
 * Shows Back/Next buttons with step indicator.
 */

import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../../styles/theme';

interface WizardNavigationProps {
  currentStep: number;
  totalSteps: number;
  onBack: () => void;
  onNext: () => void;
  canProceed: boolean;
  isLastStep: boolean;
  isGenerating?: boolean;
}

export const WizardNavigation: React.FC<WizardNavigationProps> = ({
  currentStep,
  totalSteps,
  onBack,
  onNext,
  canProceed,
  isLastStep,
  isGenerating = false,
}) => {
  const { t } = useTranslation();
  const showBackButton = currentStep > 1;

  return (
    <View style={styles.container}>
      {/* Back Button */}
      {showBackButton ? (
        <TouchableOpacity
          style={styles.backButton}
          onPress={onBack}
          disabled={isGenerating}
        >
          <Text style={styles.backButtonText}>{t('wizard.back')}</Text>
        </TouchableOpacity>
      ) : (
        <View style={styles.backButton} />
      )}

      {/* Next/Generate Button */}
      <TouchableOpacity
        style={[
          styles.nextButton,
          (!canProceed || isGenerating) && styles.nextButtonDisabled,
        ]}
        onPress={onNext}
        disabled={!canProceed || isGenerating}
      >
        <Text style={styles.nextButtonText}>
          {isGenerating ? t('wizard.generating') : isLastStep ? t('wizard.generate') : t('wizard.next')}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: PaprikaSpacing.lg,
    paddingVertical: PaprikaSpacing.md,
    borderTopWidth: 1,
    borderTopColor: Paprika.borderLight,
    backgroundColor: Paprika.background,
  },

  // Back Button
  backButton: {
    minWidth: 100,
    paddingVertical: PaprikaSpacing.sm + 2,
    paddingHorizontal: PaprikaSpacing.lg,
    borderRadius: PaprikaRadius.xl,
    borderWidth: 1,
    borderColor: Paprika.border,
    backgroundColor: Paprika.background,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backButtonText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.textPrimary,
  },

  // Next Button
  nextButton: {
    minWidth: 100,
    paddingVertical: PaprikaSpacing.sm + 2,
    paddingHorizontal: PaprikaSpacing.lg,
    borderRadius: PaprikaRadius.xl,
    backgroundColor: Paprika.textPrimary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nextButtonDisabled: {
    backgroundColor: Paprika.textMuted,
  },
  nextButtonText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.background,
  },
});

export default WizardNavigation;
